/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable max-len */

// pcg-viewer canvas: pan / zoom / hover / seek / keyboard interaction
// (core-viewer.js 4463-4670).
//
// The prototype attached these straight onto document/window and never removed
// them. Every attach* function here returns a detach() that removes exactly the
// listeners it added — that pair is the React wrapper's lifecycle contract.
// Handler bodies are verbatim under the same shim-prologue discipline as the
// draw modules; the differences are:
//   - canvas/tooltip elements are arguments, not getElementById
//   - `seekPlayback`/`redraw` are injected callbacks (the wrapper owns the
//     audio element and the draw schedule)
//   - the keyboard handler takes an isActive() predicate so shortcuts are
//     component-scoped instead of page-global (prototype: window-global)
//   - handoff v0.8.3 Loop: attachWavePanZoom's wheel handler and the new
//     attachLoopRuler take a loop controller (loop.js) in place of the
//     prototype's top-level loop functions / loopBarDrag globals

import { intervalAt as coreIntervalAt, visualGuidePeaks as coreVisualGuidePeaks } from "../core/cycles"
import { tToX as viewTToX, xToT as viewXToT, clampView as viewClampView, setZoomCentered } from "./view"

// Wave-plot geometry shared by the pan/zoom and loop-ruler handlers: the
// canvas rect plus the fixed left/right paddings of the plot area.
function wavePlotMetricsFor(waveCanvas) {
  const r = waveCanvas.getBoundingClientRect()
  const pL = 46,
    pR = 10,
    pW = r.width - pL - pR
  return { r, pL, pR, pW }
}

// ---------- Hover tooltip (core-viewer.js 4463-4518) ----------
// Waveform data follows the selected Visual guides (upstream v0.8.3.18):
// nothing shows with no data-bearing guide on; the Energy line needs the
// Energy guide, the interval lines Sys/Dia, the marker lines S1/S2. The
// Tips On / Off preference does not apply here.
export function attachWaveTooltip(rc, waveCanvas, tip, { isPanning }) {
  const D = rc.payload
  const ENV = rc.traces.env
  const MURMUR = rc.traces.murmur
  const { view, state } = rc
  const xToT = (x, pL, pW) => viewXToT(view, x, pL, pW)
  const visualGuidePeaks = () => coreVisualGuidePeaks(D, rc.core.ctx.phaseReviewResolution)
  const intervalAt = (t) => coreIntervalAt(D, rc.core.ctx.phaseReviewResolution, t)

  function onMouseMove(e) {
    if (isPanning()) return // tooltip suppressed during pan
    const showS1S2Data = !!state.layers.s1s2
    const showPhaseData = !!state.layers.regions
    const showEnergyData = !!state.layers.murmur
    if (!showS1S2Data && !showPhaseData && !showEnergyData) {
      tip.style.display = "none"
      return
    }
    const r = waveCanvas.getBoundingClientRect()
    const x = e.clientX - r.left
    const pL = 46,
      pR = 10,
      pW = r.width - pL - pR
    if (x < pL || x > r.width - pR) {
      tip.style.display = "none"
      return
    }
    const t = xToT(x, pL, pW)
    if (t < view.start || t > view.start + view.width) {
      tip.style.display = "none"
      return
    }
    const hoverPeaks = showS1S2Data || showPhaseData ? visualGuidePeaks() : []
    let inside = hoverPeaks.find((p) => t >= p.on && t <= p.off)
    let np = null,
      nd = Infinity
    hoverPeaks.forEach((p) => {
      const d = Math.abs(p.t - t)
      if (d < nd) {
        nd = d
        np = p
      }
    })
    tip.style.display = "block"
    tip.style.left = x + 15 + "px"
    tip.style.top = "20px"
    let html = `<div style="font-weight:700;color:#fff">${t.toFixed(3)}s</div>`
    const ei = Math.floor((t / D.duration) * ENV.length)
    if (showEnergyData && ei >= 0 && ei < ENV.length) {
      const energyRange = rc.core.coherentEnergyV1(state.energyBand).rangeHz || [100, 300]
      html += `<div style="color:#fca5a5">Energy ${(MURMUR[ei] * 100).toFixed(0)}% · selected ${energyRange[0]}–${
        energyRange[1]
      } Hz band</div>`
    }
    const interval = showPhaseData ? intervalAt(t) : null
    if (interval) {
      const durMs = (interval.duration * 1000).toFixed(0)
      html += `<div style="color:#e5e7eb;font-weight:700">${interval.kind} guide &middot; ${durMs}ms</div>`
      html += `<div style="color:#9ca3af">${interval.start.l} ${interval.start.t.toFixed(3)}s &rarr; ${
        interval.end.l
      } ${interval.end.t.toFixed(3)}s</div>`
      if (interval.short === "SYS") {
        const next = intervalAt(interval.end.t + 0.001)
        if (next && next.short === "DIA") {
          const ratio = interval.duration / next.duration
          html += `<div style="color:#6b7280;font-size:10px">Cycle timing ${
            ratio > 0 ? `1:${(1 / ratio).toFixed(1)}` : "-"
          }</div>`
        }
      } else {
        const prev = intervalAt(Math.max(0, interval.start.t - 0.001))
        if (prev && prev.short === "SYS") {
          const ratio = prev.duration / interval.duration
          html += `<div style="color:#6b7280;font-size:10px">Cycle timing ${
            ratio > 0 ? `1:${(1 / ratio).toFixed(1)}` : "-"
          }</div>`
        }
      }
    }
    if (showS1S2Data && inside) {
      const pColor = inside.l === "S1" ? "#cbd5e1" : "#94a3b8"
      const dur = ((inside.off - inside.on) * 1000).toFixed(0)
      const inferred = inside.source === "inferred" || inside.promoted === true
      const outsideEval = inside.outsideEvalRange === true || inside.usedForResults === false
      html += `<div style="color:${pColor};font-weight:700">${inside.l} ${
        inferred ? "inferred guide marker" : "detected timing guide"
      } &middot; ${dur}ms</div>`
      if (outsideEval) {
        html += `<div style="color:#9ca3af;font-size:10px">Outside the Evaluation Range. Shown for context; not used for the displayed results.</div>`
      } else if (inferred) {
        html += `<div style="color:#9ca3af;font-size:10px">Inferred guide marker. Accepted inferred markers may contribute to guide metrics; visual-only inferred markers do not change clinical findings.</div>`
      }
      html += `<div style="color:#6b7280;font-size:10px">onset ${inside.on.toFixed(3)}s &rarr; peak ${inside.t.toFixed(
        3
      )}s &rarr; offset ${inside.off.toFixed(3)}s</div>`
    } else if (showS1S2Data && np && nd < 0.3) {
      const pColor = np.l === "S1" ? "#cbd5e1" : "#94a3b8"
      const inferred = np.source === "inferred" || np.promoted === true
      const outsideEval = np.outsideEvalRange === true || np.usedForResults === false
      html += `<div style="color:${pColor}">${(nd * 1000).toFixed(0)}ms from ${np.l} ${
        inferred ? "inferred guide marker" : "timing guide"
      }</div>`
      if (outsideEval) {
        html += `<div style="color:#9ca3af;font-size:10px">Outside the Evaluation Range. Shown for context; not used for the displayed results.</div>`
      } else if (inferred) {
        html += `<div style="color:#9ca3af;font-size:10px">Inferred guide marker. Accepted inferred markers may contribute to guide metrics; visual-only inferred markers do not change clinical findings.</div>`
      }
    }
    tip.innerHTML = html
  }
  function onMouseLeave() {
    tip.style.display = "none"
  }
  waveCanvas.addEventListener("mousemove", onMouseMove)
  waveCanvas.addEventListener("mouseleave", onMouseLeave)
  return function detach() {
    waveCanvas.removeEventListener("mousemove", onMouseMove)
    waveCanvas.removeEventListener("mouseleave", onMouseLeave)
  }
}

// ---------- Pan / zoom / playhead seek on the main waveform (core-viewer.js 4520-4608) ----------
// `loop` (optional) is a createLoopController() instance; when present the
// wheel handler brackets the zoom in a loop viewport edit (handoff v0.8.3).
/**
 * @param {object} rc render context
 * @param {HTMLCanvasElement} waveCanvas
 * @param {HTMLElement | null} tip
 * @param {{ seekPlayback: Function, redraw: Function, getAudio: Function, loop?: any }} options
 */
export function attachWavePanZoom(rc, waveCanvas, tip, { seekPlayback, redraw, getAudio, loop = null }) {
  const D = rc.payload
  const { view } = rc
  const tToX = (t, pL, pW) => viewTToX(view, t, pL, pW)
  const xToT = (x, pL, pW) => viewXToT(view, x, pL, pW)
  const clampView = () => viewClampView(view, D.duration)
  const currentPlaybackTime = () => rc.getPlaybackTime()

  const panState = { dragging: false, moved: false, mode: null, x0: 0, viewStart0: 0 }
  const wavePlotMetrics = () => wavePlotMetricsFor(waveCanvas)
  function waveEventTime(e) {
    const { r, pL, pW } = wavePlotMetrics()
    const x = e.clientX - r.left
    return Math.max(0, Math.min(D.duration, xToT(x, pL, pW)))
  }
  function playheadHitTest(e) {
    const t = currentPlaybackTime()
    if (t === null || t < view.start || t > view.start + view.width) return false
    const { r, pL, pW } = wavePlotMetrics()
    const x = e.clientX - r.left
    if (x < pL || x > r.width - 10) return false
    const playheadX = tToX(t, pL, pW)
    return Math.abs(x - playheadX) <= 9
  }
  function panStart(e) {
    if (e.button !== 0) return
    panState.dragging = true
    panState.moved = false
    panState.mode = playheadHitTest(e) ? "seek" : "pan"
    panState.x0 = e.clientX
    panState.viewStart0 = view.start
    waveCanvas.classList.toggle("playhead-dragging", panState.mode === "seek")
    if (tip) tip.style.display = "none"
    if (panState.mode === "seek") {
      seekPlayback(waveEventTime(e))
    }
  }
  function panMove(e) {
    if (!panState.dragging) return
    const { pW } = wavePlotMetrics()
    const dx = e.clientX - panState.x0
    if (Math.abs(dx) > 4) panState.moved = true
    if (panState.mode === "seek") {
      seekPlayback(waveEventTime(e))
      return
    }
    view.start = panState.viewStart0 - (dx / pW) * view.width
    clampView()
    redraw()
  }
  function panEnd() {
    panState.dragging = false
    panState.mode = null
    waveCanvas.classList.remove("playhead-dragging")
  }
  function onHoverMove(e) {
    if (panState.dragging) return
    waveCanvas.classList.toggle("playhead-hover", playheadHitTest(e))
  }
  function onHoverLeave() {
    if (!panState.dragging) waveCanvas.classList.remove("playhead-hover")
  }
  function onClick(e) {
    if (panState.moved) {
      panState.moved = false
      return
    }
    const audio = getAudio()
    if (!audio || !audio.src) return
    const { r, pL, pW } = wavePlotMetrics()
    const x = e.clientX - r.left
    if (x < pL || x > r.width - 10) return
    seekPlayback(xToT(x, pL, pW))
  }
  function onWheel(e) {
    e.preventDefault()
    const r = waveCanvas.getBoundingClientRect()
    const pL = 46,
      pR = 10,
      pW = r.width - pL - pR
    const x = e.clientX - r.left
    if (x < pL || x > r.width - pR) return
    if (loop) {
      loop.cancelViewportCommit()
      loop.beginLoopViewportEdit()
    }
    const tCursor = xToT(x, pL, pW)
    const factor = e.deltaY > 0 ? 1.25 : 0.8
    const newWidth = view.width * factor
    view.start = tCursor - ((x - pL) / pW) * newWidth
    view.width = newWidth
    clampView()
    redraw()
    // A trackpad/scroll-wheel gesture emits a burst of events. Commit exactly
    // one authoritative loop range after the burst settles, rather than
    // restarting playback against every intermediate viewport.
    if (loop) loop.scheduleViewportCommit(180)
  }

  waveCanvas.addEventListener("mousedown", panStart)
  window.addEventListener("mousemove", panMove)
  window.addEventListener("mouseup", panEnd)
  waveCanvas.addEventListener("mousemove", onHoverMove)
  waveCanvas.addEventListener("mouseleave", onHoverLeave)
  waveCanvas.addEventListener("click", onClick)
  waveCanvas.addEventListener("wheel", onWheel, { passive: false })
  return {
    isPanning: () => panState.dragging,
    detach() {
      waveCanvas.removeEventListener("mousedown", panStart)
      window.removeEventListener("mousemove", panMove)
      window.removeEventListener("mouseup", panEnd)
      waveCanvas.removeEventListener("mousemove", onHoverMove)
      waveCanvas.removeEventListener("mouseleave", onHoverLeave)
      waveCanvas.removeEventListener("click", onClick)
      waveCanvas.removeEventListener("wheel", onWheel, { passive: false })
    },
  }
}

// ---------- Loop ruler: loop-bar drag / click toggle (handoff v0.8.3) ----------
// rulerEl is the transparent <button class="wave-loop-ruler"> positioned over
// the wave plot; all x→time math uses the WAVE canvas rect (prototype
// wavePlotMetrics), exactly as upstream. `loop` is a createLoopController().
// Upstream bound the ruler listeners at bootstrap and never removed them; here
// every listener (ruler, document, window) is removed by detach().
export function attachLoopRuler(rc, rulerEl, waveCanvas, loop, { getAudio, seekPlayback, startAudioPlayback }) {
  const D = rc.payload
  const { view, state } = rc
  const tToX = (t, pL, pW) => viewTToX(view, t, pL, pW)
  const xToT = (x, pL, pW) => viewXToT(view, x, pL, pW)
  const audioPlayer = () => getAudio()
  const activeLoopBounds = () => loop.activeLoopBounds()
  const cancelLoopBoundaryTransition = () => loop.cancelLoopBoundaryTransition()
  const setIndependentLoopRange = (a, b) => loop.setIndependentLoopRange(a, b)
  const toggleVisibleWindowLoop = () => loop.toggleVisibleWindowLoop()
  const wavePlotMetrics = () => wavePlotMetricsFor(waveCanvas)

  const loopBarDrag = {
    active: false,
    moved: false,
    suppressClick: false,
    mode: null,
    x0: 0,
    start0: 0,
    end0: 0,
    resume: false,
    suspended: false,
    pointerId: null,
    target: null,
  }
  function loopRulerTime(clientX) {
    const { r, pL, pW } = wavePlotMetrics()
    const x = Math.max(pL, Math.min(r.width - 10, clientX - r.left))
    return Math.max(0, Math.min(D.duration, xToT(x, pL, pW)))
  }
  function beginLoopBarDrag(event) {
    if (event.button !== 0 || !state.windowLoop) return
    const bounds = activeLoopBounds()
    if (!bounds) return
    const { r, pL, pW } = wavePlotMetrics()
    const x = event.clientX - r.left
    const sx = tToX(bounds.start, pL, pW)
    const ex = tToX(bounds.end, pL, pW)
    // Keep the visible grip restrained while preserving a generous invisible target.
    // so either edge remains easy to acquire at high zoom levels.
    const edgeHit = 18
    loopBarDrag.active = true
    loopBarDrag.moved = false
    loopBarDrag.x0 = event.clientX
    loopBarDrag.start0 = bounds.start
    loopBarDrag.end0 = bounds.end
    loopBarDrag.pointerId = event.pointerId
    loopBarDrag.target = event.currentTarget
    loopBarDrag.resume = Boolean(audioPlayer() && !audioPlayer().paused)
    // A loop edit is a short transport transaction: pause as soon as the
    // region is grabbed, commit one range on release, then resume from its
    // new start. Waiting for pointer movement leaves playback and editing in
    // competition and is especially unreliable in Chrome.
    loopBarDrag.suspended = true
    state.loopViewportEditing = true
    cancelLoopBoundaryTransition()
    state.loopSeekPending = false
    state.loopSeekTarget = null
    const audio = audioPlayer()
    if (audio && !audio.paused) audio.pause()
    if (Math.abs(x - sx) <= edgeHit) loopBarDrag.mode = "resize-start"
    else if (Math.abs(x - ex) <= edgeHit) loopBarDrag.mode = "resize-end"
    else if (x >= Math.min(sx, ex) && x <= Math.max(sx, ex)) loopBarDrag.mode = "move"
    else loopBarDrag.mode = "recenter"
    event.currentTarget.setPointerCapture?.(event.pointerId)
    event.currentTarget.classList.add("loop-dragging")
    event.preventDefault()
    event.stopPropagation()
  }
  function moveLoopBar(event) {
    if (!loopBarDrag.active || (loopBarDrag.pointerId !== null && event.pointerId !== loopBarDrag.pointerId)) return
    const { pW } = wavePlotMetrics()
    const delta = ((event.clientX - loopBarDrag.x0) / pW) * view.width
    if (Math.abs(event.clientX - loopBarDrag.x0) > 3) {
      loopBarDrag.moved = true
    }
    const duration = loopBarDrag.end0 - loopBarDrag.start0
    let start = loopBarDrag.start0,
      end = loopBarDrag.end0
    if (loopBarDrag.mode === "resize-start") start = Math.min(end - 0.25, Math.max(0, start + delta))
    else if (loopBarDrag.mode === "resize-end") end = Math.max(start + 0.25, Math.min(D.duration, end + delta))
    else if (loopBarDrag.mode === "move") {
      start = Math.max(0, Math.min(D.duration - duration, start + delta))
      end = start + duration
    } else {
      const center = loopRulerTime(event.clientX)
      start = Math.max(0, Math.min(D.duration - duration, center - duration / 2))
      end = start + duration
      loopBarDrag.mode = "move"
      loopBarDrag.x0 = event.clientX
      loopBarDrag.start0 = start
      loopBarDrag.end0 = end
      loopBarDrag.moved = true
    }
    setIndependentLoopRange(start, end)
    event.preventDefault()
  }
  function endLoopBarDrag(event) {
    if (!loopBarDrag.active) return
    if (
      event &&
      loopBarDrag.pointerId !== null &&
      event.pointerId !== undefined &&
      event.pointerId !== loopBarDrag.pointerId
    )
      return
    const target = loopBarDrag.target || rulerEl
    const pointerId = loopBarDrag.pointerId
    loopBarDrag.active = false
    loopBarDrag.suppressClick = loopBarDrag.moved
    target?.classList.remove("loop-dragging")
    if (pointerId !== null && target?.hasPointerCapture?.(pointerId)) {
      try {
        target.releasePointerCapture(pointerId)
      } catch (_error) {
        // Capture may already have been released by the browser.
      }
    }
    const bounds = activeLoopBounds()
    const audio = audioPlayer()
    state.loopViewportEditing = false
    if (loopBarDrag.moved && bounds && audio) {
      state.loopSeekPending = true
      state.loopSeekTarget = bounds.start
      if (!seekPlayback(bounds.start, { keepVisible: false })) state.loopSeekPending = false
      if (loopBarDrag.resume) startAudioPlayback(audio)
    } else if (loopBarDrag.resume && audio) {
      // A press without a drag becomes the ruler's normal toggle click.
      // Do not leave playback paused while that click is dispatched.
      startAudioPlayback(audio)
    }
    loopBarDrag.suspended = false
    loopBarDrag.pointerId = null
    loopBarDrag.target = null
    event?.preventDefault?.()
    event?.stopPropagation?.()
  }
  function onWindowBlur() {
    endLoopBarDrag(null)
  }
  function onClick(event) {
    event.preventDefault()
    event.stopPropagation()
    // Pointer clicks must not leave the ruler focused: otherwise the next
    // Space press activates the ruler and paints a misleading focus box
    // instead of using the global play/pause shortcut.
    if (event.detail !== 0) event.currentTarget.blur()
    if (loopBarDrag.suppressClick) {
      loopBarDrag.suppressClick = false
      return
    }
    toggleVisibleWindowLoop()
  }

  rulerEl.addEventListener("pointerdown", beginLoopBarDrag)
  rulerEl.addEventListener("pointermove", moveLoopBar)
  rulerEl.addEventListener("pointerup", endLoopBarDrag)
  rulerEl.addEventListener("pointercancel", endLoopBarDrag)
  rulerEl.addEventListener("lostpointercapture", endLoopBarDrag)
  // Chrome can dispatch the terminal event outside the ruler after a fast drag.
  // These fallbacks make release finalization reliable without depending on
  // pointer capture surviving the complete gesture.
  document.addEventListener("pointerup", endLoopBarDrag)
  document.addEventListener("pointercancel", endLoopBarDrag)
  window.addEventListener("blur", onWindowBlur)
  rulerEl.addEventListener("click", onClick)
  return {
    isDragging: () => loopBarDrag.active,
    detach() {
      rulerEl.removeEventListener("pointerdown", beginLoopBarDrag)
      rulerEl.removeEventListener("pointermove", moveLoopBar)
      rulerEl.removeEventListener("pointerup", endLoopBarDrag)
      rulerEl.removeEventListener("pointercancel", endLoopBarDrag)
      rulerEl.removeEventListener("lostpointercapture", endLoopBarDrag)
      document.removeEventListener("pointerup", endLoopBarDrag)
      document.removeEventListener("pointercancel", endLoopBarDrag)
      window.removeEventListener("blur", onWindowBlur)
      rulerEl.removeEventListener("click", onClick)
    },
  }
}

// ---------- Transport buttons: pointerup-first activation (handoff v0.8.3) ----------
// Upstream bindReliableTransportButton: the action runs on pointerup; a
// pointer-generated click (detail !== 0) is swallowed so it does not fire
// twice, while a keyboard click (detail 0) still activates. Optional helper for
// the wrapper's Start / Play-Pause / Loop buttons (bind via refs); returns detach().
export function attachReliableTransportButton(button, action) {
  async function onPointerUp(event) {
    if (event.button !== 0) return
    event.preventDefault()
    event.stopPropagation()
    await action()
  }
  async function onClick(event) {
    // Pointer input is handled on pointerup. A keyboard-generated click
    // has detail 0 and remains available for Space/Enter activation.
    if (event.detail !== 0) {
      event.preventDefault()
      event.stopPropagation()
      return
    }
    await action()
  }
  button.addEventListener("pointerup", onPointerUp)
  button.addEventListener("click", onClick)
  return function detach() {
    button.removeEventListener("pointerup", onPointerUp)
    button.removeEventListener("click", onClick)
  }
}

// ---------- Minimap navigation (core-viewer.js 4622-4660) ----------
export function attachMinimapNav(rc, miniCanvas, { redraw }) {
  const D = rc.payload
  const { view } = rc
  const clampView = () => viewClampView(view, D.duration)

  const miniState = { dragging: false, mode: null, x0: 0, viewStart0: 0 }
  function miniXToT(x) {
    const r = miniCanvas.getBoundingClientRect()
    const pL = 46,
      pR = 10,
      pW = r.width - pL - pR
    return ((x - pL) / pW) * D.duration
  }
  function onMouseDown(e) {
    const r = miniCanvas.getBoundingClientRect()
    const x = e.clientX - r.left
    const pL = 46,
      pR = 10,
      pW = r.width - pL - pR
    const vx0 = pL + (view.start / D.duration) * pW
    const vx1 = pL + ((view.start + view.width) / D.duration) * pW
    miniState.dragging = true
    miniState.x0 = e.clientX
    miniState.viewStart0 = view.start
    if (x >= vx0 && x <= vx1) {
      miniState.mode = "drag"
    } else {
      // Click outside viewport: jump to that point centered.
      const tClick = miniXToT(x)
      view.start = tClick - view.width / 2
      clampView()
      redraw()
      miniState.mode = "drag"
      miniState.viewStart0 = view.start
    }
  }
  function onWindowMove(e) {
    if (!miniState.dragging) return
    const r = miniCanvas.getBoundingClientRect()
    const pL = 46,
      pR = 10,
      pW = r.width - pL - pR
    const dx = e.clientX - miniState.x0
    view.start = miniState.viewStart0 + (dx / pW) * D.duration
    clampView()
    redraw()
  }
  function onWindowUp() {
    miniState.dragging = false
  }
  miniCanvas.addEventListener("mousedown", onMouseDown)
  window.addEventListener("mousemove", onWindowMove)
  window.addEventListener("mouseup", onWindowUp)
  return function detach() {
    miniCanvas.removeEventListener("mousedown", onMouseDown)
    window.removeEventListener("mousemove", onWindowMove)
    window.removeEventListener("mouseup", onWindowUp)
  }
}

// ---------- Keyboard (core-viewer.js 4662-4670) ----------
// Prototype: window-global, always active. Here the wrapper supplies isActive()
// (focus-or-hover within the component) so shortcuts never leak into the rest
// of the portal page.
export function attachKeyboardShortcuts(rc, { redraw, isActive }) {
  const D = rc.payload
  const { view } = rc
  const clampView = () => viewClampView(view, D.duration)

  function onKeyDown(e) {
    if (!isActive()) return
    if (e.target && (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT"))
      return
    if (e.key === "ArrowLeft") {
      view.start -= view.width * 0.25
      clampView()
      redraw()
      e.preventDefault()
    } else if (e.key === "ArrowRight") {
      view.start += view.width * 0.25
      clampView()
      redraw()
      e.preventDefault()
    } else if (e.key === "+" || e.key === "=") {
      setZoomCentered(view, D.duration, view.width * 0.5)
      redraw()
      e.preventDefault()
    } else if (e.key === "-") {
      setZoomCentered(view, D.duration, view.width * 2.0)
      redraw()
      e.preventDefault()
    } else if (e.key === "0") {
      view.start = 0
      view.width = D.duration
      clampView()
      redraw()
      e.preventDefault()
    }
  }
  window.addEventListener("keydown", onKeyDown)
  return function detach() {
    window.removeEventListener("keydown", onKeyDown)
  }
}
